import { ensure } from 'components/sharpr/ensure/ensure';
import _ from 'lodash';

export class UserPreferencesUnsubscribeBaseCtrl {
	constructor(Api, Cfg, Identity, UrlData, Logger, $timeout, $scope) {
		'ngInject';
		this.name = this.getName();
		this.Api = Api;
		this.Cfg = Cfg;
		this.Identity = Identity;
		this.Logger = Logger;
		this.$timeout = $timeout;
		this.reqHeaders = {};
		this._pendingRequests = {};
		this.error = '';
		this.unsub = {};
		this.forUser = null;
		this.isInitialLoad = true;
		this.autoUnsubClient = '';
		this.showNotifications = parseInt(this.Cfg.read('Features.show_notifications'), 10) === 1;
		let inactiveTables = this.getInactiveTables();
		this.tableInfo = {
			actAlert: {
				isLoading: false,
				hasNoData: false,
				title: 'Send me an email from this hub:',
				noRecordsMsg: 'No records found',
				action: this.toggleActAlert.bind(this),
			},
			newslet: {
				isLoading: false,
				hasNoData: false,
				title: 'Allow emails to be sent to me from these hubs:',
				noRecordsMsg: 'You have not received any emails recently',
				action: this.toggleUnsubByRef.bind(this),
			},
		};
		_.forEach(inactiveTables, name => {
			delete this.tableInfo[name];
		});

		this.wasUnsubscribed = UrlData.get('done') || 0;
		UrlData.set('done', '');
		this.ref = UrlData.get('ref');
		if (this.ref === undefined) {
			this.ref = 'me';
		}

		// We don't care about done after the first load but, if it's not bound, it remains in the url
		// after leaving the page.
		let foo = null;
		UrlData.shouldNotifyOnRemove = true;
		UrlData.bindAll($scope, { ref: this.ref, done: foo }, this.updateParams.bind(this));
	}

	getName() {
		return 'UserPreferencesUnsubscribe';
	}

	getInactiveTables() {
		if (!this.showNotifications) {
			return ['newslet'];
		}
		return [];
	}

	toggleUnsubByRef(dataTableRow) {
		dataTableRow.checked = !dataTableRow.checked;

		let endpoint = '/v2/users/unsubscribe/' + dataTableRow.hash;
		let data = {
			is_subscribed: dataTableRow.checked,
		};
		delete dataTableRow.error;
		this.Api.request('PATCH', endpoint, data, this.getReqHeaders()).then(resp => {
			if (this._isBadResponse(resp)) {
				this.Logger.write('ERROR', 'Unable to update subscription: ' + (resp.statusText || 'Unknown'), {
					params_from_attempt: this.getFormattedParams(),
				});
				dataTableRow.error = resp.statusText;
			}
		});
	}

	getReqHeaders() {
		return this.reqHeaders;
	}

	_isBadResponse(resp) {
		if (!resp || !resp.status || resp.status < 200 || resp.status > 299) {
			return true;
		}
		return false;
	}

	toggleActAlert(dataTableRow) {
		dataTableRow.checked = !dataTableRow.checked;

		let endpoint = '/v2/users/unsubscribe/activity_alerts/' + dataTableRow.id;
		let data = {
			is_subscribed: dataTableRow.checked,
			ref: this.ref,
		};
		delete dataTableRow.error;
		this.Api.request('PATCH', endpoint, data, this.getReqHeaders()).then(resp => {
			if (this._isBadResponse(resp)) {
				this.Logger.write('ERROR', 'Unable to update activity-alert subscription: ' + (resp.statusText || 'Unknown'), {
					params_from_attempt: this.getFormattedParams(),
				});
				dataTableRow.error = resp.statusText;
			}
		});
	}

	updateParams(newVals, oldVals) {
		this.resetData();
		this.ref = newVals.ref;
		this.validate();

		if (this.needsUnsubscribeId()) {
			let endpoint = '/v2/users/unsubscribe/unsub_info' + this.getFormattedParams();
			this.Api.request('GET', endpoint, null, { Authorization: 'Public' }).then(resp => {
				if (this._isBadResponse(resp)) {
					let msg = 'Unable to retrieve unsub info';
					this.Logger.write('ERROR', msg + ': ' + (resp.statusText || 'Unknown'), {
						params_from_attempt: this.getFormattedParams(),
					});
					return false;
				}

				this.unsub = resp.data;
				if (!this.isAutoUnsubForCurrentHub()) {
					let deets = this.unsub.clientDetails;
					this.autoUnsubClient = deets.affiliation + ' \u203a ' + deets.name;
				}
				this.afterUpdateParam();
			});
		} else {
			this.afterUpdateParam();
		}

		this.isInitialLoad = false;
	}

	isAutoUnsubForCurrentHub() {
		return (
			_.get(this, 'unsub.type') != 'alert' ||
			_.get(this, 'unsub.clientDetails.id', null) == this.Identity.currentClient().id
		);
	}

	resetData() {
		this.forUser = null;

		if (!this.isInitialLoad) {
			this.wasUnsubscribed = false;
			this.unsub = {};
			this.autoUnsubClient = '';
		}

		_.forEach(this.tableInfo, (val, key) => {
			val.isLoading = false;
			val.error = false;
			val.hasNoData = false;
			val.data = [];
		});

		this.setError();
	}

	setError(msg) {
		this.error = msg;
		_.forEach(this.tableInfo, (val, key) => {
			val.hasNoData = true;
		});
	}

	validate(msg) {
		if (msg) {
			this.setError(msg);
			return false;
		}

		if (!this.ref) {
			this.setError('Missing ref parameter');
			return false;
		}

		this.setError('');
		return true;
	}

	needsUnsubscribeId() {
		return this.wasUnsubscribed && this.isInitialLoad;
	}

	afterUpdateParam() {
		this.setForUser();
		this.loadData();
	}

	loadData() {
		let tableConfigs = [
			{
				type: 'newslet',
				endpoint: '/v2/users/unsubscribe/newsletters',
				ref: 'newslet',
				transformer: this.transformNewslet.bind(this),
			},
			{
				type: 'act-alert',
				endpoint: '/v2/users/unsubscribe/activity_alerts',
				ref: 'actAlert',
				transformer: this.transformActAlert.bind(this),
			},
		];
		let inactiveTables = this.getInactiveTables();
		tableConfigs = tableConfigs.filter(row => {
			return !inactiveTables.includes(row.ref);
		});

		tableConfigs.forEach(config => {
			this.tableInfo[config.ref].isLoading = true;
			this.tableInfo[config.ref].hasNoData = false;

			this._getProviderPromise(config).then(resp => {
				let ref = {};
				ref.data = ensure.array(resp.data);
				if (config.transformer) {
					ref.data = config.transformer(ref.data);
				}
				ref.isLoading = false;

				if (this._isBadResponse(resp)) {
					this.Logger.write(
						'ERROR',
						'Could not get the records for ' + config.type + ': ' + (resp.statusText || 'Unknown'),
						{
							params_from_attempt: this.getFormattedParams(),
						}
					);
					ref.error = 'Could not retrieve data';
					ref.hasNoData = false;
				} else {
					ref.error = '';
					ref.hasNoData = ref.data.length === 0;
				}

				this.tableInfo[config.ref] = Object.assign({}, this.tableInfo[config.ref], ref);
				this.$timeout(() => {});
			});
		});
	}

	transformHubs(data) {
		return data.map(row => {
			row.sub_name = row.affiliation_name + ' \u203a ' + row.client_name;
			delete row.client_name;
			delete row.affiliation_name;
			return row;
		});
	}

	transformSubs(data) {
		return data.map(row => {
			row.checked = row.is_subscribed === '1' || false;
			return row;
		});
	}

	transformNewslet(data) {
		data = this.transformHubs(data);
		data = data.map(row => {
			if (_.get(this, 'unsub.type', null) == 'newslet' && row.id == _.get(this, 'unsub.id', null)) {
				row.success = true;
			}
			return row;
		});
		return this.transformSubs(data);
	}

	transformActAlert(data) {
		data = this.transformSubs(data);
		return data.map(row => {
			if (
				this.isAutoUnsubForCurrentHub() &&
				_.get(this, 'unsub.type', null) == 'alert' &&
				row.id == _.get(this, 'unsub.id', null)
			) {
				row.success = true;
			}
			return row;
		});
	}

	_getProviderPromise(config) {
		let pendingRequest = this._pendingRequests[config.type];
		if (pendingRequest && pendingRequest.abort) {
			pendingRequest.abort();
		}
		let endpoint = config.endpoint + this.getFormattedParams();
		this._pendingRequests[config.type] = this.Api.request('GET', endpoint, null, this.getReqHeaders());
		return this._pendingRequests[config.type];
	}

	getFormattedParams() {
		return '?ref=' + this.ref;
	}

	setForUser() {
		let endpoint = '/v2/users/unsubscribe/unsub_user' + this.getFormattedParams();
		this.Api.request('GET', endpoint, null).then(resp => {
			if (this._isBadResponse(resp)) {
				let msg = 'Unable to retrieve unsub user';
				this.Logger.write('ERROR', msg + ': ' + (resp.statusText || 'Unknown'), {
					params_from_attempt: this.getFormattedParams(),
				});
				this.forUser = {
					fname: 'Unknown',
					lname: 'Unknown',
				};
				return false;
			}

			this.forUser = resp.data;
		});
	}

	doAction(action, row) {
		if (typeof action == 'function') {
			action(row);
		}
	}

	shouldShowSuccessMessage() {
		return this.wasUnsubscribed && this.unsub;
	}

	isMe() {
		if (this.forUser && this.forUser.id != this.Identity.user().id) {
			return false;
		}

		return true;
	}
}
